import { useMemo } from 'react'

/**
 * Cleans a phone number input by stripping all non-digit characters.
 * Accepts strings or numbers, returns only digits as a string.
 *
 * @param {string | number} value - The phone number input
 * @returns {string} - Digits-only phone number
 */
export const cleanPhone = (value) =>
  typeof value === 'string' || typeof value === 'number' ? String(value).replace(/\D/g, '') : ''

/**
 * Formats a phone number string into the format: (XXX) XXX-XXXX
 * Handles partial input as well (e.g., "(123", "(123) 4", etc.).
 *
 * @param {string | number} value - Raw phone number input
 * @returns {string} - Formatted phone number
 */
export const formatPhone = (value) => {
  const cleaned = cleanPhone(value)

  // Match up to 10 digits into area code, prefix, and line
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)
  if (!match) return ''

  // Construct formatted string depending on how much input is present
  const [, area, prefix, line] = match
  if (line) return `(${area}) ${prefix}-${line}`
  if (prefix) return `(${area}) ${prefix}`
  if (area) return `(${area}`
  return ''
}

/**
 * Custom hook for formatting and cleaning phone numbers.
 * Returns both masked (formatted) and raw (digits-only) values.
 *
 * @param {string | number} value - Input phone number
 * @returns {{ masked: string, raw: string }}
 */
export function usePhoneMask(value) {
  // Format once and memoize to avoid recomputation on every render
  const masked = useMemo(() => formatPhone(value), [value])
  const raw = useMemo(() => cleanPhone(value), [value])
  return { masked, raw }
}
